import { AnimatePresence, motion } from "motion/react";
import { useMemo, useState } from "react";
import { getCatalog } from "../i18n";
import { usePresentedEvents, usePresentedSnapshot } from "../presentation/hooks";
import { useNexusStore } from "../store/nexusStore";
import { panelSpring } from "../lib/motion";
import { InspectorHeader } from "./inspector/InspectorPrimitives";
import { NodeInspectorBody } from "./inspector/NodeInspectorBody";
import { OrbitalInspectorBody } from "./inspector/OrbitalInspectorBody";

export function InspectorPanel() {
  const snapshot = usePresentedSnapshot();
  const events = usePresentedEvents();
  const focus = useNexusStore((state) => state.focus);
  const focusCosmos = useNexusStore((state) => state.focusCosmos);
  const focusNode = useNexusStore((state) => state.focusNode);
  const focusOrbital = useNexusStore((state) => state.focusOrbital);
  const locale = useNexusStore((state) => state.locale);
  const requestPortal = useNexusStore((state) => state.requestPortal);
  const setCommandOpen = useNexusStore((state) => state.setCommandOpen);
  const demoTourActive = useNexusStore((state) => state.demoTourActive);
  const setDemoTourActive = useNexusStore((state) => state.setDemoTourActive);
  const ui = getCatalog(locale).ui;
  const [mobileExpandedKey, setMobileExpandedKey] = useState<string | null>(null);

  const model = useMemo(() => {
    if (!snapshot || focus.kind === "cosmos") return null;
    const node = snapshot.nodes.find((candidate) => candidate.id === focus.nodeId);
    if (!node) return null;
    const nodeEvents = events.filter((event) => event.sourceNodeId === node.id || event.targetNodeId === node.id);
    if (focus.kind === "orbital") {
      const orbital = snapshot.orbitalNodes.find((candidate) => candidate.id === focus.orbitalId);
      if (!orbital) return null;
      return {
        kind: "orbital" as const,
        node,
        orbital,
        siblings: snapshot.orbitalNodes.filter((item) => item.parentId === node.id),
        events: nodeEvents,
      };
    }
    return {
      kind: "node" as const,
      node,
      orbitals: snapshot.orbitalNodes.filter((item) => item.parentId === node.id),
      edges: snapshot.edges.filter((edge) => edge.source === node.id || edge.target === node.id),
      events: nodeEvents,
    };
  }, [events, focus, snapshot]);

  const takeControl = () => {
    if (demoTourActive) setDemoTourActive(false);
  };
  const handleCosmos = () => {
    takeControl();
    focusCosmos();
  };
  const handleNode = (id: string) => {
    takeControl();
    focusNode(id);
  };
  const handleOrbital = (nodeId: string, orbitalId: string) => {
    takeControl();
    focusOrbital(nodeId, orbitalId);
  };

  const modelKey = model?.kind === "orbital" ? `orbital-${model.orbital.id}` : model ? `node-${model.node.id}` : "none";
  const mobileExpanded = mobileExpandedKey === modelKey;
  const toggleMobileInspector = () => {
    // The disclosure is a local inspector action. Explicitly keep the global
    // command/navigation surface closed so a coarse-pointer tap cannot turn
    // into an unintended navigation gesture while the sheet is resizing.
    setCommandOpen(false);
    setMobileExpandedKey((current) => (current === modelKey ? null : modelKey));
  };
  const initial = demoTourActive
    ? { opacity: 0, x: 18, y: 5, scale: 0.988, filter: "blur(8px)" }
    : { opacity: 0, x: 24, y: 3, scale: 0.992, filter: "blur(5px)" };
  const animate = { opacity: 1, x: 0, y: 0, scale: 1, filter: "blur(0px)" };
  const exit = demoTourActive
    ? { opacity: 0, x: -12, y: -2, scale: 0.993, filter: "blur(5px)" }
    : { opacity: 0, x: 14, y: 2, scale: 0.995, filter: "blur(3px)" };

  return (
    <AnimatePresence mode="wait">
      {model && (
        <motion.aside
          key={modelKey}
          className={`inspector${mobileExpanded ? " is-mobile-expanded" : ""}`}
          initial={initial}
          animate={animate}
          exit={exit}
          layout
          transition={demoTourActive ? { ...panelSpring, delay: 0.34 } : panelSpring}
        >
          <div className="inspector__rule" />
          {model.kind === "node" ? (
            <>
              <InspectorHeader
                eyebrow={model.node.eyebrow}
                title={model.node.label}
                closeLabel={ui.closeInspector}
                onClose={handleCosmos}
                mobileExpanded={mobileExpanded}
                mobileToggleLabel={mobileExpanded ? ui.collapseInspector : ui.expandInspector}
                onMobileToggle={toggleMobileInspector}
              />
              <div id="inspector-mobile-details" className="inspector__body">
                <NodeInspectorBody
                  node={model.node}
                  nodes={snapshot!.nodes}
                  edges={model.edges}
                  orbitals={model.orbitals}
                  events={model.events}
                  locale={locale}
                  onNode={handleNode}
                  onOrbital={handleOrbital}
                  onOpenSystem={requestPortal}
                />
              </div>
            </>
          ) : (
            <>
              <InspectorHeader
                eyebrow={ui.orbitalIdentity}
                title={model.orbital.label}
                closeLabel={ui.closeInspector}
                onClose={() => handleNode(model.node.id)}
                mobileExpanded={mobileExpanded}
                mobileToggleLabel={mobileExpanded ? ui.collapseInspector : ui.expandInspector}
                onMobileToggle={toggleMobileInspector}
              />
              <div id="inspector-mobile-details" className="inspector__body">
                <OrbitalInspectorBody
                  orbital={model.orbital}
                  parent={model.node}
                  siblings={model.siblings}
                  events={model.events}
                  locale={locale}
                  onOrbital={handleOrbital}
                />
              </div>
            </>
          )}
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
